import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from 'stores/authStore';
import { useUserProfileStore } from 'stores/userProfileStore';
import { useCompanyServiceQueries } from 'services/company';
import { CreateCompanyRequest } from 'types/company';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import Toast from 'components/Toast';
import { Building2, Loader2, UserRound } from 'lucide-react';
import {
  TIN_PLACEHOLDER,
  formatTinForInput,
  validateTin,
} from 'utils/tinUtils';
import { companyVatStatusSelectOptions } from 'utils/companyVatStatusOptions';
import styles from './CompanyLobby.module.scss';

type AccountType = 'company' | null;
const DEFAULT_COMPANY_DESCRIPTION = 'No description provided.';

function CompanyLobby() {
  const profile = useUserProfileStore((s) => s.profile);
  const refreshProfile = useUserProfileStore((s) => s.refreshProfile);
  const isLoading = useUserProfileStore((s) => s.isLoading);
  const isAuth = useAuthStore((state) => state.isAuth);
  const companyService = useCompanyServiceQueries();
  const navigate = useNavigate();

  const [accountType, setAccountType] = useState<AccountType>(null);
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyTin, setCompanyTin] = useState('');
  const [companyVatStatus, setCompanyVatStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [tinError, setTinError] = useState<string | null>(null);

  const vatStatusOptions = useMemo(
    () => companyVatStatusSelectOptions(companyVatStatus),
    [companyVatStatus],
  );

  const getTinValidationError = useCallback(validateTin, []);

  useEffect(() => {
    if (profile?.companies && profile.companies.length > 0) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [profile?.companies, navigate]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const handleAutoCreateForIndividual = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const request: CreateCompanyRequest = {
        name: 'My Own Company',
        description: DEFAULT_COMPANY_DESCRIPTION,
      };
      await companyService.createCompany(request);
      await refreshProfile();
      showToast('Company created successfully!', 'success');
      setTimeout(() => {
        navigate(ROUTES.DASHBOARD);
      }, 1200);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create company. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [companyService, navigate, refreshProfile, showToast]);

  const handleCreateCompanyFromForm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!companyName.trim()) {
        showToast('Please enter a company name', 'error');
        return;
      }

      const tinValidationError = getTinValidationError(companyTin);
      if (tinValidationError) {
        setTinError(tinValidationError);
        showToast(tinValidationError, 'error');
        return;
      }
      setTinError(null);

      const sanitizedTin = companyTin.replace(/\D/g, '');

      setIsSubmitting(true);
      try {
        const request: CreateCompanyRequest = {
          name: companyName.trim(),
          description: companyDescription.trim() || DEFAULT_COMPANY_DESCRIPTION,
          ...(companyAddress.trim() && { address: companyAddress.trim() }),
          ...(sanitizedTin && { tin: sanitizedTin }),
          ...(companyVatStatus.trim() && {
            vatStatus: companyVatStatus.trim(),
          }),
          entityType: 'company',
        };
        await companyService.createCompany(request);
        await refreshProfile();
        showToast('Company created successfully!', 'success');
        setTimeout(() => {
          navigate(ROUTES.DASHBOARD);
        }, 1200);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create company. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      companyAddress,
      companyDescription,
      companyName,
      companyService,
      companyTin,
      companyVatStatus,
      getTinValidationError,
      navigate,
      refreshProfile,
      showToast,
    ],
  );

  if (isLoading || !isAuth) {
    return null;
  }

  if (profile?.companies && profile.companies.length > 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.hero}>
          <div className={styles.iconContainer}>
            <Building2 className={styles.icon} size={48} />
          </div>

          <h1 className={styles.title}>Welcome to Abrasoft Hub</h1>
          <p className={styles.subtitle}>
            {profile?.username || 'User'}, let&apos;s set up your company access
            first.
          </p>
        </div>

        {!accountType && (
          <h2 className={styles.choicePrompt}>Would you like to...</h2>
        )}

        <div
          className={
            accountType === 'company'
              ? `${styles.options} ${styles.optionsForm}`
              : styles.options
          }
        >
          {!accountType && (
            <>
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <UserRound className={styles.cardIcon} size={24} />
                  <h3 className={styles.cardTitle}>I am an Individual</h3>
                </div>
                <p className={styles.cardDescription}>
                  We will automatically create your workspace as{' '}
                  <strong>My Own Company</strong>.
                </p>
                <Button
                  className={styles.createButton}
                  onClick={() => void handleAutoCreateForIndividual()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className={styles.buttonLoader} size={16} />
                      Creating...
                    </>
                  ) : (
                    'Continue as Individual'
                  )}
                </Button>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <Building2 className={styles.cardIcon} size={24} />
                  <h3 className={styles.cardTitle}>I represent a Company</h3>
                </div>
                <p className={styles.cardDescription}>
                  Create your company details and start inviting team members.
                </p>
                <Button
                  className={styles.createButton}
                  onClick={() => setAccountType('company')}
                  disabled={isSubmitting}
                >
                  Continue as Company
                </Button>
              </div>
            </>
          )}

          {accountType === 'company' && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Building2 className={styles.cardIcon} size={24} />
                <h3 className={styles.cardTitle}>Create a Company</h3>
              </div>
              <p className={styles.cardDescription}>
                Fill in your company details below. Description is optional.
              </p>
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  onClick={() => setAccountType(null)}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              </div>

              <form
                onSubmit={(e) => void handleCreateCompanyFromForm(e)}
                className={styles.form}
              >
                <div className={styles.formGroup}>
                  <label htmlFor="companyName" className={styles.label}>
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    className={styles.input}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="companyDescription" className={styles.label}>
                    Company Description{' '}
                    <span className={styles.optional}>(Optional)</span>
                  </label>
                  <textarea
                    id="companyDescription"
                    value={companyDescription}
                    onChange={(e) => setCompanyDescription(e.target.value)}
                    placeholder="Enter company description"
                    className={styles.textarea}
                    disabled={isSubmitting}
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="companyAddress" className={styles.label}>
                    Address <span className={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    id="companyAddress"
                    type="text"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="Enter company address"
                    className={styles.input}
                    disabled={isSubmitting}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="companyTin" className={styles.label}>
                    Tax Identification Number (TIN){' '}
                    <span className={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    id="companyTin"
                    type="text"
                    value={companyTin}
                    onChange={(e) => {
                      setCompanyTin(e.target.value);
                      if (tinError) setTinError(null);
                    }}
                    onBlur={() => {
                      const error = getTinValidationError(companyTin);
                      if (error) {
                        setTinError(error);
                        return;
                      }
                      const formatted = formatTinForInput(companyTin);
                      setCompanyTin(formatted);
                    }}
                    placeholder={TIN_PLACEHOLDER}
                    className={styles.input}
                    disabled={isSubmitting}
                  />
                  {tinError && (
                    <div className={styles.errorText}>{tinError}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="companyVatStatus" className={styles.label}>
                    VAT Status{' '}
                    <span className={styles.optional}>(Optional)</span>
                  </label>
                  <select
                    id="companyVatStatus"
                    value={companyVatStatus}
                    onChange={(e) => setCompanyVatStatus(e.target.value)}
                    className={styles.input}
                    disabled={isSubmitting}
                  >
                    {vatStatusOptions.map((opt) => (
                      <option key={opt.value || '__empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !companyName.trim()}
                  className={styles.createButton}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className={styles.buttonLoader} size={16} />
                      Creating...
                    </>
                  ) : (
                    'Create Company'
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default CompanyLobby;
